import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, PERIODS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend, Treemap,
} from "recharts";
import { FileText, MapPin, Layers, TrendingUp, Loader2, Flame, Mail } from "lucide-react";

const COLORS = ["#1e40af", "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#84cc16", "#64748b"];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [period, setPeriod] = useState("30");
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || (user.role !== "personale" && user.role !== "admin"))) navigate("/login");
  }, [user, loading, navigate]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/stats?period=${period}`);
      setStats(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (user?.role === "personale" || user?.role === "admin") load(); }, [period, user]);

  if (loading || !(user?.role === "personale" || user?.role === "admin")) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">...</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Area Amministratori</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-1">Andamento attività</h1>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-48" data-testid="admin-period-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => <SelectItem key={p.value} value={p.value} data-testid={`period-opt-${p.value}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button asChild variant="outline" data-testid="admin-quotes-link"><Link to="/admin/preventivi">Vedi preventivi →</Link></Button>
            <Button asChild variant="outline" data-testid="admin-mailing-link"><Link to="/admin/mailing"><Mail className="w-4 h-4 mr-2"/>Mailing list</Link></Button>
          </div>
        </div>

        {busy && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>}

        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admin-kpi-cards">
              <KPI icon={<FileText className="w-5 h-5"/>} label="Totale preventivi" value={stats.total}/>
              <KPI icon={<Layers className="w-5 h-5"/>} label="Categorie attive" value={stats.by_category.length}/>
              <KPI icon={<MapPin className="w-5 h-5"/>} label="Città coperte" value={stats.by_city.length}/>
              <KPI icon={<TrendingUp className="w-5 h-5"/>} label="Nel periodo" value={stats.timeline.reduce((a,b)=>a+b.count,0)}/>
            </div>

            <Card className="p-6 mb-6">
              <h3 className="heading font-bold text-slate-900 mb-4">Richieste nel tempo</h3>
              <div className="h-72" data-testid="admin-timeline-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.timeline}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1e40af" stopOpacity={0.6}/>
                        <stop offset="100%" stopColor="#1e40af" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12}/>
                    <YAxis stroke="#64748b" fontSize={12} allowDecimals={false}/>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}/>
                    <Area type="monotone" dataKey="count" stroke="#1e40af" fill="url(#g1)" strokeWidth={2}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="p-6">
                <h3 className="heading font-bold text-slate-900 mb-4">Per categoria</h3>
                <div className="h-72" data-testid="admin-category-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_category}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                      <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-20} textAnchor="end" height={70}/>
                      <YAxis stroke="#64748b" fontSize={12} allowDecimals={false}/>
                      <Tooltip contentStyle={{ borderRadius: 8 }}/>
                      <Bar dataKey="value" fill="#1e40af" radius={[4, 4, 0, 0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="heading font-bold text-slate-900 mb-4">Per città</h3>
                <div className="h-72" data-testid="admin-city-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.by_city} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                        {stats.by_city.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                      </Pie>
                      <Tooltip/>
                      <Legend/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            {stats.by_city.length > 0 && (
              <Card className="p-6 mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-5 h-5 text-orange-500"/>
                  <h3 className="heading font-bold text-slate-900">Zone Calde</h3>
                </div>
                <p className="text-xs text-slate-500 mb-4">Le città con più richieste: dove investire di più</p>
                <div className="h-80" data-testid="admin-hotzones-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={hotZoneData(stats.by_city)}
                      dataKey="value"
                      stroke="#fff"
                      content={<HotZoneCell/>}
                    />
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4" data-testid="admin-hotzones-list">
                  {[...stats.by_city].sort((a,b)=>b.value-a.value).slice(0,8).map((c, i) => (
                    <div key={c.name} className="flex items-center gap-2 text-sm">
                      <span className={`font-bold text-lg ${i===0?'text-red-600':i===1?'text-orange-500':i===2?'text-amber-500':'text-slate-400'}`}>#{i+1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.value} richieste</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KPI({ icon, label, value }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-blue-800">{icon}<span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <div className="heading text-3xl font-extrabold text-slate-900 mt-2">{value}</div>
    </Card>
  );
}

function hotZoneData(cities) {
  const sorted = [...cities].sort((a,b)=>b.value-a.value);
  const max = sorted[0]?.value || 1;
  const heat = ["#dc2626", "#ea580c", "#f59e0b", "#eab308", "#84cc16", "#22c55e", "#10b981", "#06b6d4", "#3b82f6", "#6366f1"];
  return sorted.map((c, i) => ({
    name: c.name, value: c.value,
    fill: heat[Math.min(i, heat.length - 1)],
    intensity: Math.round((c.value / max) * 100),
  }));
}

function HotZoneCell(props) {
  const { x, y, width, height, name, value, fill } = props;
  if (width < 30 || height < 30) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2}/>
      <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle" fill="#fff" fontWeight={700} fontSize={Math.min(16, width / 8)} style={{ pointerEvents: 'none' }}>{name}</text>
      <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" fill="#fff" fontSize={11} opacity={0.9} style={{ pointerEvents: 'none' }}>{value}</text>
    </g>
  );
}
